// =====================================================================
// myCRM — Admin
// =====================================================================

const INVITE_TTL_OPTIONS = [
  { hours: 2,  label: "2 hours" },
  { hours: 4,  label: "4 hours" },
  { hours: 8,  label: "8 hours" },
  { hours: 12, label: "12 hours" },
  { hours: 24, label: "24 hours" },
  { hours: 48, label: "2 days" },
];

function InviteUserDialog({ onClose, onInvited }) {
  const [email, setEmail]     = useState("");
  const [role, setRole]       = useState("editor");
  const [ttlHours, setTtl]    = useState(8);
  const [saving, setSaving]   = useState(false);
  const [error, setError]     = useState(null);
  const [done, setDone]       = useState(null); // { url, expires_at }

  async function handleSave() {
    if (!email.trim()) { setError("Email is required."); return; }
    setSaving(true); setError(null);
    try {
      const row = await tpAPI.request("/apicrm/auth/invites", {
        method: "POST",
        body: { email: email.trim().toLowerCase(), role, ttlHours },
      });
      const url = window.location.origin + "/intcrm/register.html?token=" + encodeURIComponent(row.token);
      setDone({ url, expires_at: row.expires_at });
      if (typeof onInvited === "function") onInvited();
    } catch (e) {
      setError(e.message || "Failed to create invite.");
    } finally {
      setSaving(false);
    }
  }

  function copy(text) {
    if (navigator.clipboard && navigator.clipboard.writeText) navigator.clipboard.writeText(text);
  }

  return (
    <Dialog
      title={done ? "Invite created" : "Invite user"}
      onClose={onClose}
      footer={done ? (
        <button className="btn btn-primary btn-sm" onClick={onClose}>Done</button>
      ) : (<>
        <button className="btn btn-sm" onClick={onClose}>Cancel</button>
        <button className="btn btn-primary btn-sm" onClick={handleSave} disabled={saving}>{saving ? "Sending…" : "Create invite"}</button>
      </>)}
    >
      {done ? (<>
        <p style={{ margin: 0, fontSize: 13 }}>Share this single-use link. Expires {done.expires_at}.</p>
        <div className="form-field">
          <label>Invite URL</label>
          <input value={done.url} readOnly onFocus={e => e.target.select()} />
        </div>
        <button className="btn btn-sm" onClick={() => copy(done.url)}>Copy link</button>
      </>) : (<>
        <div className="form-field">
          <label>Email *</label>
          <input type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="person@trustedpath.dev" autoFocus />
        </div>
        <div className="form-field">
          <label>Role</label>
          <select value={role} onChange={e => setRole(e.target.value)}>
            <option value="admin">Admin — full access incl. invites</option>
            <option value="editor">Editor — read/write</option>
            <option value="viewer">Viewer — read only</option>
          </select>
        </div>
        <div className="form-field">
          <label>Expires in</label>
          <select value={ttlHours} onChange={e => setTtl(Number(e.target.value))}>
            {INVITE_TTL_OPTIONS.map(o => (
              <option key={o.hours} value={o.hours}>{o.label}</option>
            ))}
          </select>
        </div>
        {error && <div className="form-error">{error}</div>}
      </>)}
    </Dialog>
  );
}

// Issues an admin-generated password-reset link for an existing login
// account. The link only lets the user pick a new password — TOTP is
// untouched — and their current password works until the link is used.
function ResetPasswordDialog({ user, onClose }) {
  const [saving, setSaving] = useState(false);
  const [error, setError]   = useState(null);
  const [done, setDone]     = useState(null); // { url, expires_at }

  async function generate() {
    setSaving(true); setError(null);
    try {
      const row = await tpAPI.request("/apicrm/auth/users/" + encodeURIComponent(user.id) + "/reset", {
        method: "POST",
      });
      const url = window.location.origin + "/intcrm/register.html?token=" + encodeURIComponent(row.token);
      setDone({ url, expires_at: row.expires_at });
    } catch (e) {
      setError(e.message || "Failed to create reset link.");
    } finally {
      setSaving(false);
    }
  }

  function copy(text) {
    if (navigator.clipboard && navigator.clipboard.writeText) navigator.clipboard.writeText(text);
  }

  return (
    <Dialog
      title={done ? "Reset link created" : "Reset password"}
      onClose={onClose}
      footer={done ? (
        <button className="btn btn-primary btn-sm" onClick={onClose}>Done</button>
      ) : (<>
        <button className="btn btn-sm" onClick={onClose}>Cancel</button>
        <button className="btn btn-primary btn-sm" onClick={generate} disabled={saving}>{saving ? "Generating…" : "Generate reset link"}</button>
      </>)}
    >
      {done ? (<>
        <p style={{ margin: 0, fontSize: 13 }}>Share this single-use link with {user.email}. Expires {done.expires_at}.</p>
        <div className="form-field">
          <label>Reset URL</label>
          <input value={done.url} readOnly onFocus={e => e.target.select()} />
        </div>
        <button className="btn btn-sm" onClick={() => copy(done.url)}>Copy link</button>
      </>) : (<>
        <p style={{ margin: 0, fontSize: 13 }}>
          Generate a single-use link for <strong>{user.email}</strong> to set a new password.
          Their authenticator (TOTP) setup is not affected, and their current password keeps
          working until the link is used.
        </p>
        {error && <div className="form-error">{error}</div>}
      </>)}
    </Dialog>
  );
}

function PendingInvitesCard({ refreshKey }) {
  const [rows, setRows] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    tpAPI.request("/apicrm/auth/invites")
      .then(r => { if (!cancelled) setRows(Array.isArray(r) ? r : []); })
      .catch(e => { if (!cancelled) setError(e.message || "Failed to load invites."); });
    return () => { cancelled = true; };
  }, [refreshKey]);

  async function handleRevoke(id) {
    if (!confirm("Revoke this invite? The link will stop working.")) return;
    try {
      await tpAPI.request("/apicrm/auth/invites/" + id, { method: "DELETE" });
      setRows(prev => prev.map(r => r.id === id ? { ...r, status: "revoked" } : r));
    } catch (e) {
      alert(e.message || "Failed to revoke invite.");
    }
  }

  if (error) return <div className="card"><div className="card-body form-error">{error}</div></div>;
  if (rows === null) return <div className="card"><div className="card-body muted">Loading invites…</div></div>;
  if (rows.length === 0) return <div className="card"><div className="card-body muted">No invites yet. Use “Invite user” above to send one.</div></div>;

  return (
    <div className="card">
      <div className="card-head"><div className="card-title">Invites</div><div className="card-sub">{rows.length} total · most recent first</div></div>
      <table className="tbl tbl-flush">
        <thead><tr><th>Email</th><th>Role</th><th>Type</th><th>Status</th><th>Expires</th><th></th></tr></thead>
        <tbody>{rows.map(r => (
          <tr key={r.id} style={{ cursor: "default" }}>
            <td>{r.email}</td>
            <td><Badge tone="slate">{r.role}</Badge></td>
            <td><Badge tone={r.purpose === "reset" ? "amber" : "slate"}>{r.purpose === "reset" ? "Password reset" : "Account invite"}</Badge></td>
            <td><Badge tone={r.status === "consumed" ? "green" : r.status === "revoked" ? "red" : r.status === "expired" ? "slate" : "amber"}>{r.status}</Badge></td>
            <td className="muted">{r.expires_at}</td>
            <td>{r.status === "pending" && <button className="btn btn-sm" onClick={() => handleRevoke(r.id)}>Revoke</button>}</td>
          </tr>
        ))}</tbody>
      </table>
    </div>
  );
}

// Login accounts (auth_users) — list, change role, deactivate/reactivate.
// Distinct from the "Sales reps" table below, which is the application
// users table (quota/attainment), not login identities.
function LoginAccountsCard({ currentUserId }) {
  const [rows, setRows]       = useState(null);
  const [error, setError]     = useState(null);
  const [busyId, setBusyId]   = useState(null);
  const [resetFor, setResetFor] = useState(null);

  useEffect(() => {
    let cancelled = false;
    tpAPI.authUsers.list()
      .then(r => { if (!cancelled) setRows(Array.isArray(r) ? r : []); })
      .catch(e => { if (!cancelled) setError(e.message || "Failed to load login accounts."); });
    return () => { cancelled = true; };
  }, []);

  async function apply(id, changes) {
    setBusyId(id);
    try {
      const updated = await tpAPI.authUsers.update(id, changes);
      setRows(prev => prev.map(r => (r.id === id ? updated : r)));
    } catch (e) {
      alert(e.message || "Update failed.");
    } finally {
      setBusyId(null);
    }
  }

  if (error) return <div className="card" style={{ marginTop: 12 }}><div className="card-body form-error">{error}</div></div>;
  if (rows === null) return <div className="card" style={{ marginTop: 12 }}><div className="card-body muted">Loading login accounts…</div></div>;

  return (
    <>
    <div className="card" style={{ marginTop: 12 }}>
      <div className="card-head">
        <div className="card-title">Login accounts</div>
        <div className="card-sub">{rows.length} total · email / password / TOTP identities</div>
      </div>
      {rows.length === 0 ? (
        <div className="card-body muted">No login accounts yet. Invite a user above to create one.</div>
      ) : (
        <table className="tbl tbl-flush">
          <thead><tr><th>Email</th><th>Name</th><th>Role</th><th>Last login</th><th>Status</th><th></th></tr></thead>
          <tbody>{rows.map(r => {
            const isSelf = r.id === currentUserId;
            const active = !r.deleted_at;
            const status = !active ? { tone: "red", label: "Disabled" }
                         : !r.totp_enabled ? { tone: "amber", label: "Pending setup" }
                         : { tone: "green", label: "Active" };
            return (
              <tr key={r.id} style={{ cursor: "default" }}>
                <td>{r.email}{isSelf && <span className="muted"> (you)</span>}</td>
                <td>{r.name}</td>
                <td>
                  <select
                    value={r.role}
                    disabled={isSelf || busyId === r.id}
                    onChange={e => apply(r.id, { role: e.target.value })}
                    style={{ fontSize: 12 }}
                  >
                    <option value="admin">Admin</option>
                    <option value="editor">Editor</option>
                    <option value="viewer">Viewer</option>
                  </select>
                </td>
                <td className="muted">{r.last_login_at || "—"}</td>
                <td><Badge tone={status.tone}>{status.label}</Badge></td>
                <td>
                  {!isSelf && (
                    <div style={{ display: "flex", gap: 6 }}>
                      {active && r.totp_enabled && (
                        <button
                          className="btn btn-sm"
                          disabled={busyId === r.id}
                          onClick={() => setResetFor(r)}
                        >Reset password</button>
                      )}
                      <button
                        className="btn btn-sm"
                        disabled={busyId === r.id}
                        onClick={() => {
                          if (active && !confirm("Deactivate " + r.email + "? Their session stops working immediately.")) return;
                          apply(r.id, { active: !active });
                        }}
                      >{active ? "Deactivate" : "Reactivate"}</button>
                    </div>
                  )}
                </td>
              </tr>
            );
          })}</tbody>
        </table>
      )}
    </div>
    {resetFor && <ResetPasswordDialog user={resetFor} onClose={() => setResetFor(null)} />}
    </>
  );
}

// Role matrix is served by the backend (GET /apicrm/permissions) so the
// API route gating and this reference table cannot drift apart.
function PermissionsMatrix() {
  const [rows, setRows] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    tpAPI.permissions.list()
      .then(r => { if (!cancelled) setRows(Array.isArray(r) ? r : []); })
      .catch(e => { if (!cancelled) setError(e.message || "Failed to load permissions."); });
    return () => { cancelled = true; };
  }, []);

  return (
    <div className="card">
      <div className="card-head">
        <div className="card-title">Role permissions</div>
        <div className="card-sub">Enforced by the API; roles are assigned at invite time</div>
      </div>
      {error ? (
        <div className="card-body form-error">{error}</div>
      ) : rows === null ? (
        <div className="card-body muted">Loading…</div>
      ) : (
        <table className="tbl tbl-flush">
          <thead><tr><th>Role</th><th>Reads</th><th>Writes</th><th>Manage invites &amp; audit</th><th>Notes</th></tr></thead>
          <tbody>{rows.map(r => (
            <tr key={r.role} style={{ cursor: "default" }}>
              <td><Badge tone={r.role === "admin" ? "amber" : r.role === "editor" ? "slate" : "green"}>{r.label}</Badge></td>
              <td>{r.reads}</td>
              <td>{r.writes}</td>
              <td>{r.invites}</td>
              <td className="muted">{r.note}</td>
            </tr>
          ))}</tbody>
        </table>
      )}
    </div>
  );
}

function AuditLog() {
  const [rows, setRows] = useState(null);
  const [error, setError] = useState(null);
  const [actionFilter, setActionFilter] = useState("");
  const [tick, setTick] = useState(0);

  useEffect(() => {
    let cancelled = false;
    const qs = actionFilter ? `?action=${encodeURIComponent(actionFilter)}` : "";
    tpAPI.request("/apicrm/audit" + qs)
      .then(r => { if (!cancelled) setRows(Array.isArray(r) ? r : []); })
      .catch(e => { if (!cancelled) setError(e.message || "Failed to load audit log."); });
    return () => { cancelled = true; };
  }, [actionFilter, tick]);

  if (error) return <div className="card"><div className="card-body form-error">{error}</div></div>;

  return (
    <div className="card">
      <div className="card-head" style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12 }}>
        <div>
          <div className="card-title">Audit log</div>
          <div className="card-sub">{rows === null ? "Loading…" : `${rows.length} most recent events`}</div>
        </div>
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          <select value={actionFilter} onChange={e => setActionFilter(e.target.value)} style={{ fontSize: 12 }}>
            <option value="">All actions</option>
            <option value="auth.login.success">auth.login.success</option>
            <option value="auth.login.failure">auth.login.failure</option>
            <option value="auth.logout">auth.logout</option>
            <option value="auth.register">auth.register</option>
            <option value="auth.password.change">auth.password.change</option>
            <option value="invite.create">invite.create</option>
            <option value="invite.revoke">invite.revoke</option>
            <option value="auth.user.role">auth.user.role</option>
            <option value="auth.user.deactivate">auth.user.deactivate</option>
            <option value="auth.user.reactivate">auth.user.reactivate</option>
          </select>
          <button className="btn btn-sm" onClick={() => setTick(t => t + 1)}>Refresh</button>
        </div>
      </div>
      {rows && rows.length === 0 ? (
        <div className="card-body muted">No events yet for this filter.</div>
      ) : (
        <table className="tbl tbl-flush">
          <thead><tr><th>When</th><th>Actor</th><th>Action</th><th>Object</th><th>IP</th><th>Detail</th></tr></thead>
          <tbody>{(rows || []).map(r => (
            <tr key={r.id} style={{ cursor: "default" }}>
              <td className="mono muted">{r.ts}</td>
              <td>{r.actor_email || <span className="muted">—</span>}</td>
              <td><Badge tone={r.action.startsWith("auth.login.failure") ? "red" : r.action.startsWith("invite.") ? "amber" : "slate"}>{r.action}</Badge></td>
              <td className="mono">{r.object || ""}</td>
              <td className="mono muted">{r.ip || ""}</td>
              <td className="muted">{r.detail || ""}</td>
            </tr>
          ))}</tbody>
        </table>
      )}
    </div>
  );
}

// Create a sales rep in the application `users` table — the source for
// every owner / interviewer dropdown across the app.
// Loose email check that matches the backend's helpers/request.js so the
// frontend rejects obvious mistakes before round-tripping the request.
const EMAIL_PATTERN_FE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

function NewRepDialog({ onClose }) {
  const [form, setForm] = useState({ name: "", email: "", role: "AE", team: "", region: "NA", quota: "" });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  function set(k, v) { setForm(f => ({ ...f, [k]: v })); }
  async function handleSave() {
    if (!form.name.trim()) { setError("Name is required."); return; }
    const email = form.email.trim();
    if (!email || !EMAIL_PATTERN_FE.test(email)) {
      setError("A valid email is required.");
      return;
    }
    setSaving(true); setError(null);
    try {
      await tpAPI.users.create({
        id: "u_" + Math.random().toString(36).slice(2, 9),
        name: form.name.trim(), email, role: form.role, team: form.team.trim(),
        region: form.region, quota: form.quota ? Number(form.quota) : null,
        attainment: 0,
      });
      onClose();
    } catch (e) { setError(e.message || "Failed to create rep."); setSaving(false); }
  }
  return (
    <Dialog title="New sales rep" onClose={onClose} footer={<>
      <button className="btn btn-sm" onClick={onClose}>Cancel</button>
      <button className="btn btn-primary btn-sm" onClick={handleSave} disabled={saving}>{saving ? "Creating…" : "Create rep"}</button>
    </>}>
      <div className="form-row">
        <div className="form-field"><label>Name *</label>
          <input value={form.name} onChange={e => set("name", e.target.value)} placeholder="Jane Smith" autoFocus />
        </div>
        <div className="form-field"><label>Email *</label>
          <input type="email" value={form.email} onChange={e => set("email", e.target.value)} placeholder="jane@company.com" />
        </div>
      </div>
      <div className="form-row">
        <div className="form-field"><label>Role</label>
          <select value={form.role} onChange={e => set("role", e.target.value)}>
            <option>AE</option><option>SDR</option><option>BDR</option>
            <option>SE</option><option>CSM</option><option>Manager</option>
          </select>
        </div>
        <div className="form-field"><label>Region</label>
          <select value={form.region} onChange={e => set("region", e.target.value)}>
            {D.REGIONS.map(r => <option key={r} value={r}>{r}</option>)}
          </select>
        </div>
      </div>
      <div className="form-row">
        <div className="form-field"><label>Team</label>
          <input value={form.team} onChange={e => set("team", e.target.value)} placeholder="EMEA Strategic" />
        </div>
        <div className="form-field"><label>Quota (annual)</label>
          <input type="number" value={form.quota} onChange={e => set("quota", e.target.value)} placeholder="1000000" />
        </div>
      </div>
      {error && <div className="form-error">{error}</div>}
    </Dialog>
  );
}

// Enable / disable a sales rep. Disabling is a soft-retire: the rep keeps
// any accounts/opps they already own, but drops out of the owner /
// interviewer / SE pickers and rep rollups (see assignableReps in ui.jsx).
// tpAPI.users.update triggers tpReload('users'), so the row re-renders.
function RepStatusButton({ rep }) {
  const [busy, setBusy] = useState(false);
  async function toggle() {
    if (!rep.disabled &&
        !confirm("Disable " + rep.name + "? They'll keep existing records but won't be selectable for new assignments.")) {
      return;
    }
    setBusy(true);
    try {
      await tpAPI.users.update(rep.id, { disabled: !rep.disabled });
    } catch (e) {
      alert(e.message || "Failed to update rep.");
    } finally {
      setBusy(false);
    }
  }
  return (
    <button className="btn btn-sm" disabled={busy} onClick={toggle}>
      {busy ? "…" : rep.disabled ? "Enable" : "Disable"}
    </button>
  );
}

// Admin -> Customisation: app-wide default currency and FX rates.
function CustomisationPanel() {
  const [settings, setSettings] = useState(null);
  const [error, setError]       = useState(null);
  const [saving, setSaving]     = useState(false);
  const [saved, setSaved]       = useState(false);

  useEffect(() => {
    let cancelled = false;
    tpAPI.settings.get()
      .then(s => { if (!cancelled) setSettings(s); })
      .catch(e => { if (!cancelled) setError(e.message || "Failed to load settings."); });
    return () => { cancelled = true; };
  }, []);

  if (error && !settings) return <div className="card"><div className="card-body form-error">{error}</div></div>;
  if (!settings) return <div className="card"><div className="card-body muted">Loading…</div></div>;

  function setRate(ccy, v) { setSettings(s => ({ ...s, fxRates: { ...s.fxRates, [ccy]: v } })); setSaved(false); }

  async function save() {
    setSaving(true); setError(null);
    try {
      const updated = await tpAPI.settings.save({
        defaultCurrency: settings.defaultCurrency,
        fxRates: { GBP: 1, USD: Number(settings.fxRates.USD), EUR: Number(settings.fxRates.EUR) },
      });
      setSettings(updated);
      if (window.TPCRM_DATA) window.TPCRM_DATA.SETTINGS = updated;
      setSaved(true);
    } catch (e) {
      setError(e.message || "Failed to save settings.");
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="card">
      <div className="card-head">
        <div className="card-title">Currency</div>
        <div className="card-sub">Default currency for new deals and conversion rates — applies app-wide</div>
      </div>
      <div className="card-body">
        <div className="form-field" style={{ maxWidth: 240 }}>
          <label>Default currency</label>
          <select value={settings.defaultCurrency}
                  onChange={e => { setSettings(s => ({ ...s, defaultCurrency: e.target.value })); setSaved(false); }}>
            <option value="GBP">£ — GBP (Pound)</option>
            <option value="USD">$ — USD (Dollar)</option>
            <option value="EUR">€ — EUR (Euro)</option>
          </select>
        </div>

        <div className="section-title">Conversion rates</div>
        <div className="muted" style={{ fontSize: 12.5, marginBottom: 8 }}>
          Units of each currency per <b>£1 GBP</b> (GBP is the base rate).
        </div>
        <table className="tbl tbl-flush" style={{ maxWidth: 380 }}>
          <thead><tr><th>Currency</th><th>Rate (per £1)</th></tr></thead>
          <tbody>
            <tr style={{ cursor: "default" }}><td>£ GBP — base</td><td className="numeric">1.0000</td></tr>
            <tr style={{ cursor: "default" }}>
              <td>$ USD</td>
              <td><input type="number" step="0.0001" min="0" value={settings.fxRates.USD}
                         onChange={e => setRate("USD", e.target.value)} style={{ width: 130 }} /></td>
            </tr>
            <tr style={{ cursor: "default" }}>
              <td>€ EUR</td>
              <td><input type="number" step="0.0001" min="0" value={settings.fxRates.EUR}
                         onChange={e => setRate("EUR", e.target.value)} style={{ width: 130 }} /></td>
            </tr>
          </tbody>
        </table>
        {error && <div className="form-error" style={{ marginTop: 10 }}>{error}</div>}
      </div>
      <div className="card-foot flex-between">
        <span className="muted">{saved ? "✓ Saved" : "Admin-only"}</span>
        <button className="btn btn-primary btn-sm" onClick={save} disabled={saving}>{saving ? "Saving…" : "Save settings"}</button>
      </div>
    </div>
  );
}

// Admin -> Research: persona -> external public form URL. Shared by the
// Survey and Interview form panels — same six MEDDPICCR personas, same
// validation rules, same persistence shape on the backend. The settings
// key (surveyForms / interviewForms) is the only thing that differs.
//
// Each persona has its URL input with an inline Copy button. There's no
// separate "Saved links" preview — the input already shows the value,
// so two displays of the same data would just be noise.
function FormsPanel({ kind, title, subtitle, settingsKey, saveLabel }) {
  const [draft, setDraft]   = useState(null);
  const [error, setError]   = useState(null);
  const [saving, setSaving] = useState(false);
  const [justSaved, setJustSaved] = useState(false);
  const [copiedSlug, setCopiedSlug] = useState(null);

  useEffect(() => {
    let cancelled = false;
    tpAPI.settings.get()
      .then(s => { if (!cancelled) setDraft(s[settingsKey] || {}); })
      .catch(e => { if (!cancelled) setError(e.message || "Failed to load settings."); });
    return () => { cancelled = true; };
  }, [settingsKey]);

  if (error && !draft) return <div className="card" style={{ marginTop: 12 }}><div className="card-body form-error">{error}</div></div>;
  if (!draft) return <div className="card" style={{ marginTop: 12 }}><div className="card-body muted">Loading…</div></div>;

  function setUrl(slug, v) { setDraft(f => ({ ...f, [slug]: v })); setJustSaved(false); }

  async function save() {
    setSaving(true); setError(null);
    try {
      const updated = await tpAPI.settings.save({ [settingsKey]: draft });
      setDraft(updated[settingsKey] || {});
      if (window.TPCRM_DATA) window.TPCRM_DATA.SETTINGS = updated;
      setJustSaved(true);
    } catch (e) {
      setError(e.message || `Failed to save ${kind} forms.`);
    } finally {
      setSaving(false);
    }
  }

  async function copyToClipboard(slug) {
    const url = (draft[slug] || "").trim();
    if (!url) return;
    try {
      await navigator.clipboard.writeText(url);
      setCopiedSlug(slug);
      setTimeout(() => setCopiedSlug(prev => (prev === slug ? null : prev)), 1500);
    } catch (e) { /* clipboard blocked — degrade silently; the user can still select and Ctrl-C the input */ }
  }

  return (
    <div className="card" style={{ marginTop: 12 }}>
      <div className="card-head">
        <div className="card-title">{title}</div>
        <div className="card-sub">{subtitle}</div>
      </div>
      <div className="card-body">
        {SURVEY_PERSONAS.map(p => {
          const hasValue = !!((draft[p.slug] || "").trim());
          return (
            <div className="form-field" key={p.slug}>
              <label>{p.label}</label>
              <div style={{ display: "flex", gap: 6 }}>
                <input
                  type="url"
                  placeholder="https://…"
                  value={draft[p.slug] || ""}
                  onChange={e => setUrl(p.slug, e.target.value)}
                  style={{ flex: 1 }}
                />
                <button
                  type="button"
                  className="btn btn-sm"
                  onClick={() => copyToClipboard(p.slug)}
                  disabled={!hasValue}
                  title={hasValue ? "Copy URL to clipboard" : "Add a URL first"}
                >
                  {copiedSlug === p.slug ? "Copied ✓" : "Copy"}
                </button>
              </div>
            </div>
          );
        })}
        {error && <div className="form-error" style={{ marginTop: 10 }}>{error}</div>}
      </div>
      <div className="card-foot flex-between">
        <span className="muted">{justSaved ? "✓ Saved" : "Admin-only"}</span>
        <button className="btn btn-primary btn-sm" onClick={save} disabled={saving}>{saving ? "Saving…" : saveLabel}</button>
      </div>
    </div>
  );
}

function SurveyFormsPanel() {
  return (
    <FormsPanel
      kind="survey"
      title="Survey forms"
      subtitle="External public form link sent for each persona — used by “Send survey” on the Research surveys page."
      settingsKey="surveyForms"
      saveLabel="Save survey forms"
    />
  );
}

function InterviewFormsPanel() {
  return (
    <FormsPanel
      kind="interview"
      title="Interview booking links"
      subtitle="External scheduling URL (Calendly, Cal.com, etc.) used when inviting a contact to an interview, keyed by the contact's MEDDPICCR persona."
      settingsKey="interviewForms"
      saveLabel="Save interview links"
    />
  );
}

// Admin -> Customisation: lead disqualification reasons and their default
// routing. Each reason maps to either Nurture (parked + recall date) or
// Disqualified (terminal). The DQ dialog reads from here so each org can
// tune the workflow without code changes.
function DqReasonsPanel() {
  const [rows, setRows]     = useState(null);
  const [error, setError]   = useState(null);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved]   = useState(false);

  useEffect(() => {
    let cancelled = false;
    tpAPI.settings.get()
      .then(s => { if (!cancelled) setRows(Array.isArray(s.dqReasons) ? s.dqReasons : []); })
      .catch(e => { if (!cancelled) setError(e.message || "Failed to load settings."); });
    return () => { cancelled = true; };
  }, []);

  if (error && !rows) return <div className="card" style={{ marginTop: 12 }}><div className="card-body form-error">{error}</div></div>;
  if (!rows) return <div className="card" style={{ marginTop: 12 }}><div className="card-body muted">Loading…</div></div>;

  function setRow(i, k, v) {
    setRows(rs => rs.map((r, idx) => idx === i ? { ...r, [k]: v } : r));
    setSaved(false);
  }
  function addRow() {
    setRows(rs => [...rs, { label: "", outcome: "Disqualified", recallMonths: null }]);
    setSaved(false);
  }
  function removeRow(i) {
    setRows(rs => rs.filter((_, idx) => idx !== i));
    setSaved(false);
  }

  async function save() {
    setSaving(true); setError(null);
    try {
      const payload = rows.map(r => ({
        label: (r.label || "").trim(),
        outcome: r.outcome === "Nurture" ? "Nurture" : "Disqualified",
        recallMonths: r.outcome === "Nurture" && r.recallMonths !== "" && r.recallMonths != null
          ? Number(r.recallMonths)
          : null,
      }));
      const updated = await tpAPI.settings.save({ dqReasons: payload });
      setRows(updated.dqReasons || []);
      if (window.TPCRM_DATA) window.TPCRM_DATA.SETTINGS = updated;
      setSaved(true);
    } catch (e) {
      setError(e.message || "Failed to save DQ reasons.");
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="card" style={{ marginTop: 12 }}>
      <div className="card-head">
        <div className="card-title">Lead disqualification reasons</div>
        <div className="card-sub">Reasons + default routing for the lead <strong>Disqualify / Nurture</strong> dialog. Nurture entries park the lead with a follow-up task; Disqualified is terminal.</div>
      </div>
      <table className="tbl tbl-flush">
        <thead><tr><th>Reason</th><th>Default outcome</th><th>Recall (months)</th><th></th></tr></thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i} style={{ cursor: "default" }}>
              <td><input value={r.label || ""} onChange={e => setRow(i, "label", e.target.value)} placeholder="Reason name" /></td>
              <td>
                <select value={r.outcome} onChange={e => setRow(i, "outcome", e.target.value)}>
                  <option value="Nurture">Nurture</option>
                  <option value="Disqualified">Disqualified</option>
                </select>
              </td>
              <td>
                {r.outcome === "Nurture"
                  ? <input type="number" min="0" max="60" value={r.recallMonths == null ? "" : r.recallMonths}
                           onChange={e => setRow(i, "recallMonths", e.target.value)} style={{ width: 80 }} />
                  : <span className="muted">—</span>}
              </td>
              <td className="right"><button className="btn btn-sm" onClick={() => removeRow(i)}>Remove</button></td>
            </tr>
          ))}
          {rows.length === 0 && <tr><td colSpan={4} className="muted" style={{ textAlign: "center" }}>No reasons — add one below.</td></tr>}
        </tbody>
      </table>
      {error && <div className="card-body form-error">{error}</div>}
      <div className="card-foot flex-between">
        <div className="flex-gap">
          <button className="btn btn-sm" onClick={addRow}>+ Add reason</button>
          <span className="muted">{saved ? "✓ Saved" : "Admin-only"}</span>
        </div>
        <button className="btn btn-primary btn-sm" onClick={save} disabled={saving}>{saving ? "Saving…" : "Save reasons"}</button>
      </div>
    </div>
  );
}

// Admin -> Customisation: customer health score weights, activity-recency
// bands, recent-loss penalty, and the red/amber/green colour thresholds.
// The compute function in ui.jsx reads from D.SETTINGS.healthScore so
// changes take effect on the next data refresh.
const HEALTH_WEIGHT_KEYS = [
  { key: "activityRecency",     label: "Activity recency",     hint: "Days since last activity (calls/emails/meetings/tasks)" },
  { key: "stakeholderCoverage", label: "Stakeholder coverage", hint: "Champion + multi-threaded on the most recent opp" },
  { key: "opportunityOutcomes", label: "Opportunity outcomes", hint: "Win ratio across closed deals on this account" },
  { key: "championStrength",    label: "Champion strength",    hint: "Strong vs weak champion in research responses" },
  { key: "contactEngagement",   label: "Contact engagement",   hint: "Opt-outs and research-fatigue across the account's contacts" },
];

function HealthScorePanel() {
  const [cfg, setCfg]       = useState(null);
  const [error, setError]   = useState(null);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved]   = useState(false);

  useEffect(() => {
    let cancelled = false;
    tpAPI.settings.get()
      .then(s => { if (!cancelled) setCfg(s.healthScore || null); })
      .catch(e => { if (!cancelled) setError(e.message || "Failed to load settings."); });
    return () => { cancelled = true; };
  }, []);

  if (error && !cfg) return <div className="card" style={{ marginTop: 12 }}><div className="card-body form-error">{error}</div></div>;
  if (!cfg) return <div className="card" style={{ marginTop: 12 }}><div className="card-body muted">Loading…</div></div>;

  const dirty = () => setSaved(false);
  function setWeight(k, v) { setCfg(c => ({ ...c, weights: { ...c.weights, [k]: v } })); dirty(); }
  function setBand(i, k, v) { setCfg(c => ({ ...c, activityRecencyBands: c.activityRecencyBands.map((b, idx) => idx === i ? { ...b, [k]: v } : b) })); dirty(); }
  function addBand() {
    setCfg(c => {
      const last = c.activityRecencyBands[c.activityRecencyBands.length - 1];
      const maxDays = last ? Number(last.maxDays) + 30 : 14;
      return { ...c, activityRecencyBands: [...c.activityRecencyBands, { maxDays, fraction: 0 }] };
    });
    dirty();
  }
  function removeBand(i) { setCfg(c => ({ ...c, activityRecencyBands: c.activityRecencyBands.filter((_, idx) => idx !== i) })); dirty(); }
  function setField(k, v) { setCfg(c => ({ ...c, [k]: v })); dirty(); }
  function setThreshold(k, v) { setCfg(c => ({ ...c, thresholds: { ...c.thresholds, [k]: v } })); dirty(); }

  const weightTotal = HEALTH_WEIGHT_KEYS.reduce((s, w) => s + (Number(cfg.weights[w.key]) || 0), 0);

  async function save() {
    setSaving(true); setError(null);
    try {
      const payload = {
        weights: Object.fromEntries(HEALTH_WEIGHT_KEYS.map(w => [w.key, Number(cfg.weights[w.key]) || 0])),
        activityRecencyBands: cfg.activityRecencyBands.map(b => ({
          maxDays: Number(b.maxDays), fraction: Number(b.fraction),
        })),
        recentLossDays:    Number(cfg.recentLossDays),
        recentLossPenalty: Number(cfg.recentLossPenalty),
        thresholds: { green: Number(cfg.thresholds.green), amber: Number(cfg.thresholds.amber) },
      };
      const updated = await tpAPI.settings.save({ healthScore: payload });
      setCfg(updated.healthScore || payload);
      if (window.TPCRM_DATA) window.TPCRM_DATA.SETTINGS = updated;
      setSaved(true);
    } catch (e) {
      setError(e.message || "Failed to save health score config.");
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="card" style={{ marginTop: 12 }}>
      <div className="card-head">
        <div className="card-title">Customer health score</div>
        <div className="card-sub">
          Weights and rules for the customer-health dot on the Accounts list and the Health tab of each account.
          Score is normalised against whichever components have data, so weights don't need to sum to 100 — but
          keeping them at 100 makes the breakdown easier to read.
        </div>
      </div>

      <div className="card-body">
        <div className="section-title">Component weights</div>
        <table className="tbl tbl-flush" style={{ maxWidth: 720 }}>
          <thead><tr><th>Component</th><th className="right">Weight</th><th>What it measures</th></tr></thead>
          <tbody>
            {HEALTH_WEIGHT_KEYS.map(w => (
              <tr key={w.key} style={{ cursor: "default" }}>
                <td>{w.label}</td>
                <td className="right">
                  <input type="number" min="0" max="100" style={{ width: 80, textAlign: "right" }}
                         value={cfg.weights[w.key] == null ? "" : cfg.weights[w.key]}
                         onChange={e => setWeight(w.key, e.target.value)} />
                </td>
                <td className="muted">{w.hint}</td>
              </tr>
            ))}
            <tr style={{ cursor: "default" }}>
              <td><strong>Total</strong></td>
              <td className="right numeric"><strong>{weightTotal}</strong></td>
              <td className="muted">{weightTotal === 100 ? "Sums to 100 — readable breakdown." : "Will be normalised at render time."}</td>
            </tr>
          </tbody>
        </table>

        <div className="section-title">Activity-recency bands</div>
        <div className="muted" style={{ fontSize: 12.5, marginBottom: 6 }}>
          Days since the most recent logged activity → fraction of the Activity-recency weight. Bands are
          checked top-down; the first one where days ≤ maxDays wins. Anything past the last band scores 0.
        </div>
        <table className="tbl tbl-flush" style={{ maxWidth: 480 }}>
          <thead><tr><th>If days ≤</th><th>Award fraction</th><th></th></tr></thead>
          <tbody>
            {cfg.activityRecencyBands.map((b, i) => (
              <tr key={i} style={{ cursor: "default" }}>
                <td><input type="number" min="0" max="3650" style={{ width: 100 }}
                           value={b.maxDays == null ? "" : b.maxDays}
                           onChange={e => setBand(i, "maxDays", e.target.value)} /></td>
                <td><input type="number" min="0" max="1" step="0.05" style={{ width: 100 }}
                           value={b.fraction == null ? "" : b.fraction}
                           onChange={e => setBand(i, "fraction", e.target.value)} /></td>
                <td className="right"><button className="btn btn-sm" onClick={() => removeBand(i)} disabled={cfg.activityRecencyBands.length <= 1}>Remove</button></td>
              </tr>
            ))}
          </tbody>
        </table>
        <div style={{ marginTop: 8 }}><button className="btn btn-sm" onClick={addBand}>+ Add band</button></div>

        <div className="section-title">Recent-loss penalty</div>
        <div className="muted" style={{ fontSize: 12.5, marginBottom: 6 }}>
          A Closed-Lost opportunity within this window deducts the penalty from the Opportunity-outcomes score (clamped to 0).
        </div>
        <div className="form-row">
          <div className="form-field" style={{ maxWidth: 200 }}>
            <label>Window (days)</label>
            <input type="number" min="0" max="3650"
                   value={cfg.recentLossDays == null ? "" : cfg.recentLossDays}
                   onChange={e => setField("recentLossDays", e.target.value)} />
          </div>
          <div className="form-field" style={{ maxWidth: 200 }}>
            <label>Penalty (points)</label>
            <input type="number" min="0" max="100"
                   value={cfg.recentLossPenalty == null ? "" : cfg.recentLossPenalty}
                   onChange={e => setField("recentLossPenalty", e.target.value)} />
          </div>
        </div>

        <div className="section-title">Colour thresholds</div>
        <div className="muted" style={{ fontSize: 12.5, marginBottom: 6 }}>
          Score ≥ green → green dot; score ≥ amber → amber; below → red. Amber must be ≤ green.
        </div>
        <div className="form-row">
          <div className="form-field" style={{ maxWidth: 200 }}>
            <label>Green ≥</label>
            <input type="number" min="0" max="100"
                   value={cfg.thresholds.green == null ? "" : cfg.thresholds.green}
                   onChange={e => setThreshold("green", e.target.value)} />
          </div>
          <div className="form-field" style={{ maxWidth: 200 }}>
            <label>Amber ≥</label>
            <input type="number" min="0" max="100"
                   value={cfg.thresholds.amber == null ? "" : cfg.thresholds.amber}
                   onChange={e => setThreshold("amber", e.target.value)} />
          </div>
        </div>

        {error && <div className="form-error" style={{ marginTop: 10 }}>{error}</div>}
      </div>

      <div className="card-foot flex-between">
        <span className="muted">{saved ? "✓ Saved · refresh the page to see updated scores" : "Admin-only"}</span>
        <button className="btn btn-primary btn-sm" onClick={save} disabled={saving}>{saving ? "Saving…" : "Save health score config"}</button>
      </div>
    </div>
  );
}

// Admin -> Customisation: org-wide default commission percentages.
// Per-deal overrides land on the opportunity row itself, so changing
// these later doesn't retroactively re-price closed deals. Partner
// rate here is the fallback when an opp has a partner attached but
// the partner row has no per-partner default of its own.
function CommissionDefaultsPanel() {
  const ROLES = [
    { key: "owner",   label: "Owner (AE)" },
    { key: "sdr",     label: "SDR" },
    { key: "se",      label: "Solutions Engineer" },
    { key: "partner", label: "Partner (fallback)" },
  ];
  const [cfg, setCfg]       = useState(null);
  const [error, setError]   = useState(null);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved]   = useState(false);

  useEffect(() => {
    let cancelled = false;
    tpAPI.settings.get()
      .then(s => { if (!cancelled) setCfg(s.commissionDefaults || { owner: 8, sdr: 1, se: 0.5, partner: 15 }); })
      .catch(e => { if (!cancelled) setError(e.message || "Failed to load settings."); });
    return () => { cancelled = true; };
  }, []);

  if (error && !cfg) return <div className="card" style={{ marginTop: 12 }}><div className="card-body form-error">{error}</div></div>;
  if (!cfg) return <div className="card" style={{ marginTop: 12 }}><div className="card-body muted">Loading…</div></div>;

  function setRate(role, v) {
    setCfg(c => ({ ...c, [role]: v === "" ? "" : Number(v) }));
    setSaved(false);
  }

  async function save() {
    setSaving(true); setError(null);
    try {
      const payload = Object.fromEntries(ROLES.map(r => [r.key, Number(cfg[r.key]) || 0]));
      const updated = await tpAPI.settings.save({ commissionDefaults: payload });
      setCfg(updated.commissionDefaults || payload);
      if (window.TPCRM_DATA) window.TPCRM_DATA.SETTINGS = updated;
      setSaved(true);
    } catch (e) {
      setError(e.message || "Failed to save commission defaults.");
    } finally {
      setSaving(false);
    }
  }

  const total = ROLES.reduce((s, r) => s + (Number(cfg[r.key]) || 0), 0);

  return (
    <div className="card" style={{ marginTop: 12 }}>
      <div className="card-head">
        <div className="card-title">Commission defaults</div>
        <div className="card-sub">
          Default percentages used to seed the per-deal commission split on a new opportunity. Per-deal overrides
          land on the opportunity row so changing these later doesn't retroactively re-price closed deals.
        </div>
      </div>
      <div className="card-body">
        <table className="tbl tbl-flush" style={{ maxWidth: 480 }}>
          <thead><tr><th>Role</th><th className="right">Default rate</th></tr></thead>
          <tbody>
            {ROLES.map(r => (
              <tr key={r.key} style={{ cursor: "default" }}>
                <td>{r.label}</td>
                <td className="right">
                  <input type="number" min="0" max="100" step="0.1" style={{ width: 100, textAlign: "right" }}
                         value={cfg[r.key] == null ? "" : cfg[r.key]}
                         onChange={e => setRate(r.key, e.target.value)} />
                  <span className="muted" style={{ marginLeft: 4 }}>%</span>
                </td>
              </tr>
            ))}
            <tr style={{ cursor: "default" }}>
              <td><strong>Total</strong></td>
              <td className="right numeric"><strong>{total}%</strong></td>
            </tr>
          </tbody>
        </table>
        <div className="muted" style={{ fontSize: 12, marginTop: 10 }}>
          The <strong>Partner (fallback)</strong> rate is only applied when an opp has a partner attached but the
          partner record has no per-partner default. Set per-partner rates in <code>Sales → Partners → Default commission %</code>.
        </div>
        {error && <div className="form-error" style={{ marginTop: 10 }}>{error}</div>}
      </div>
      <div className="card-foot flex-between">
        <span className="muted">{saved ? "✓ Saved" : "Admin-only"}</span>
        <button className="btn btn-primary btn-sm" onClick={save} disabled={saving}>{saving ? "Saving…" : "Save commission defaults"}</button>
      </div>
    </div>
  );
}

function AdminPage() {
  const { user } = useAuth();
  const [tab, setTab] = useState("users");
  const [showInvite, setShowInvite] = useState(false);
  const [showRep, setShowRep] = useState(false);
  const [invitesTick, setInvitesTick] = useState(0);

  // Admin-only: editor/viewer roles get a friendly forbidden view rather
  // than hitting the API and seeing 403s on every panel.
  if (!user || user.role !== "admin") {
    return (
      <>
        <PageHead title="Admin" sub="Restricted" />
        <div className="card"><div className="card-body">
          <p>Settings is restricted to administrators. Ask your admin to grant access.</p>
        </div></div>
      </>
    );
  }

  const tabs = [
    { key: "users", label: "Users & teams" },
    { key: "reps",  label: "Sales reps" },
    { key: "perm", label: "Permissions" },
    { key: "custom", label: "Customisation" },
    { key: "research", label: "Research" },
    { key: "auth", label: "Authentication" },
    { key: "integ", label: "Integrations" },
    { key: "audit", label: "Audit log" },
  ];
  return (
    <>
      <PageHead
        title="Admin"
        sub="Org settings, users, auth & integrations"
        actions={
          tab === "users" ? <button className="btn btn-primary btn-sm" onClick={() => setShowInvite(true)}>+ Invite user</button>
          : tab === "reps" ? <button className="btn btn-primary btn-sm" onClick={() => setShowRep(true)}>+ New rep</button>
          : null
        }
      />
      <Tabs tabs={tabs} current={tab} onChange={setTab} />
      {tab === "users" && (<>
        <PendingInvitesCard refreshKey={invitesTick} />
        <LoginAccountsCard currentUserId={user.id} />
        {showInvite && <InviteUserDialog onClose={() => setShowInvite(false)} onInvited={() => setInvitesTick(t => t + 1)} />}
      </>)}
      {tab === "reps" && (<>
        <div className="card">
          <div className="card-head">
            <div className="card-title">Sales reps</div>
            <div className="card-sub">From the sales users table — drives owner &amp; interviewer dropdowns across the app. Distinct from <em>Login accounts</em> on the Users &amp; teams tab.</div>
          </div>
          {D.USERS.length === 0 ? (
            <div className="card-body muted">No sales reps yet. Add one to populate the owner and interviewer dropdowns across the app.</div>
          ) : (
            <table className="tbl tbl-flush">
              <thead><tr><th>User</th><th>Email</th><th>Role</th><th>Team</th><th>Region</th><th className="right">Quota</th><th>Status</th><th></th></tr></thead>
              <tbody>{D.USERS.map(u => (
                <tr key={u.id} style={{ cursor: "default" }}>
                  <td><span className="flex-gap" style={{alignItems:"center"}}><Avatar user={u} size={22}/> {u.name}</span></td>
                  <td className="muted">{u.email || <span style={{ color: "var(--red)" }}>— (missing)</span>}</td>
                  <td><Badge tone="slate">{u.role}</Badge></td>
                  <td>{u.team}</td>
                  <td className="muted">{u.region || "—"}</td>
                  <td className="right numeric">{u.quota ? fmtMoney(u.quota) : "—"}</td>
                  <td>{u.disabled ? <Badge tone="red">Disabled</Badge> : <Badge tone="green">Active</Badge>}</td>
                  <td className="right"><RepStatusButton rep={u} /></td>
                </tr>
              ))}</tbody>
            </table>
          )}
        </div>
        {showRep && <NewRepDialog onClose={() => setShowRep(false)} />}
      </>)}
      {tab === "auth" && (
        <div className="grid grid-2">
          <div className="card">
            <div className="card-head"><div className="card-title">Authentication providers</div><div className="card-sub">Pluggable — swap by replacing the auth component</div></div>
            <div className="card-body">
              {[
                { name: "Local password", status: "Enabled", note: "Default — useful for dev/preview environments" },
                { name: "SAML 2.0 SSO", status: "Configured", note: "Okta · ACS endpoint registered · slot ready" },
                { name: "OIDC", status: "Configured", note: "Auth0 / Azure AD — slot ready" },
                { name: "SCIM provisioning", status: "Available", note: "Auto-provision + deprovision users" },
                { name: "Magic link", status: "Disabled", note: "Email-based passwordless option" },
                { name: "API tokens", status: "Enabled", note: "Service accounts and integrations" },
              ].map(r => (
                <div key={r.name} className="medd-row">
                  <div className="medd-letter">⛨</div>
                  <div><div className="medd-label">{r.name}</div><div className="medd-desc">{r.note}</div></div>
                  <Badge tone={r.status === "Enabled" || r.status === "Configured" ? "green" : "slate"}>{r.status}</Badge>
                </div>
              ))}
            </div>
          </div>
          <div className="card">
            <div className="card-head"><div className="card-title">Identity slot</div><div className="card-sub">Future drop-in</div></div>
            <div className="card-body">
              <div className="muted" style={{ fontSize: 13, lineHeight: 1.6 }}>
                Authentication is implemented as a pluggable component. The current build ships with local password auth for preview. To activate SSO, replace the <span className="mono" style={{ background: "var(--bg-sunken)", padding: "1px 6px", borderRadius: 4 }}>&lt;AuthProvider /&gt;</span> with your IdP-specific component (Okta, Auth0, Azure AD, etc.) — all downstream session, role and audit hooks are unchanged.
              </div>
              <div className="section-title">Defined contracts</div>
              <ul style={{ paddingLeft: 18, color: "var(--text-2)", fontSize: 12.5, lineHeight: 1.8 }}>
                <li>signIn() → resolves with session token + user profile</li>
                <li>signOut() → clears session, fires audit event</li>
                <li>onSessionChange(callback) → subscribe to changes</li>
                <li>getRoles() → returns role/permission set for current user</li>
                <li>getProvisioning() → SCIM hook for user lifecycle</li>
              </ul>
            </div>
          </div>
        </div>
      )}
      {tab === "perm" && <PermissionsMatrix />}
      {tab === "custom" && (<>
        <CustomisationPanel />
        <DqReasonsPanel />
        <CommissionDefaultsPanel />
        <HealthScorePanel />
      </>)}
      {tab === "research" && (<>
        <SurveyFormsPanel />
        <InterviewFormsPanel />
      </>)}
      {tab === "integ" && (
        <div className="card"><div className="card-body">
          {[
            { name: "Salesforce", status: "Connected", note: "Bi-directional sync · last 4m ago" },
            { name: "HubSpot Marketing", status: "Connected", note: "Lead sync, MQL events" },
            { name: "Slack", status: "Connected", note: "#deal-rooms, #insights" },
            { name: "Gong", status: "Connected", note: "Call recordings + sentiment" },
            { name: "Looker", status: "Connected", note: "Embedded dashboards in Forecast" },
            { name: "DocuSign", status: "Connected", note: "Quote/MSA signature flow" },
            { name: "Zoom", status: "Connected", note: "Meeting capture" },
            { name: "Snowflake", status: "Disconnected", note: "Reverse-ETL planned" },
          ].map(r => (
            <div key={r.name} className="medd-row">
              <div className="medd-letter">⇄</div>
              <div><div className="medd-label">{r.name}</div><div className="medd-desc">{r.note}</div></div>
              <Badge tone={r.status === "Connected" ? "green" : "slate"}>{r.status}</Badge>
            </div>
          ))}
        </div></div>
      )}
      {tab === "audit" && <AuditLog />}
    </>
  );
}

window.AdminPage = AdminPage;
